import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  Select,
  MenuItem,
  Typography,
  Grid,
  Divider,
} from '@mui/material';

const JOIN_TYPES = [
  { value: 'LEFT', label: 'Left' },
  { value: 'RIGHT', label: 'Right' },
  { value: 'INNER', label: 'Inner' },
  { value: 'OUTER', label: 'Outer' },
];

const CARDINALITY_OPTIONS = [
  { value: 'one-one', label: 'One – One' },
  { value: 'one-many', label: 'One – Many' },
  { value: 'many-one', label: 'Many – One' },
  { value: 'many-many', label: 'Many – Many' },
];

const AGGREGATE_OPERATIONS = [
  { value: 'SUM', label: 'Sum' },
  { value: 'MIN', label: 'Min' },
  { value: 'MAX', label: 'Max' },
  { value: 'AVG', label: 'Avg' },
  { value: 'COUNT', label: 'Count' },
];

const normalizeTargetSchema = (targetSchema) => {
  if (!targetSchema) return [];
  if (Array.isArray(targetSchema)) return targetSchema;
  return [{ tableName: targetSchema, columns: [], id: targetSchema }];
};

const getColumnOptions = (sourceOrTarget) => {
  if (!sourceOrTarget?.columns?.length) return [];
  return sourceOrTarget.columns
    .map((c) => {
      const name = typeof c === 'object' ? (c.column_name ?? c.name ?? c.columnName ?? '') : c;
      return { value: name, label: name };
    })
    .filter((o) => o.value != null && String(o.value).trim() !== '');
};

const NAGARRO_PRIMARY = '#00043c';

const selectSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: '#fafafa',
    fontSize: '0.875rem',
    minHeight: 44,
    '&:hover': { backgroundColor: '#f5f5f5' },
    '&.Mui-focused': { backgroundColor: '#fff' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: NAGARRO_PRIMARY, borderWidth: '2px' },
    '& fieldset': { borderColor: '#d1d5db' },
    '&:hover fieldset': { borderColor: '#9ca3af' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: NAGARRO_PRIMARY },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      maxWidth: '100%',
      borderTop: `2px solid ${NAGARRO_PRIMARY}`,
      mt: 0.5,
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'rgba(0, 4, 60, 0.12)',
        color: NAGARRO_PRIMARY,
      },
      '& .MuiMenuItem-root.Mui-selected:hover': {
        backgroundColor: 'rgba(0, 4, 60, 0.18)',
      },
      '& .MuiMenuItem-root:hover': {
        backgroundColor: 'rgba(0, 4, 60, 0.08)',
      },
    },
  },
};

const JoinWithAggregateDialog = ({
  open,
  onClose,
  sources = [],
  targetSchema,
  fetchedHeader = [],
  initialValues = null,
  existingMappings = [],
  onSave,
  isEdit = false,
  reviewMode = false,
  onReject,
  onApprove,
}) => {
  const targets = useMemo(() => normalizeTargetSchema(targetSchema), [targetSchema]);

  const [source1Table, setSource1Table] = useState(initialValues?.source1Table || initialValues?.sourceTable || '');
  const [source1Col, setSource1Col] = useState(initialValues?.source1Col || initialValues?.sourceCol || '');
  const [source2Table, setSource2Table] = useState(initialValues?.source2Table || '');
  const [source2Col, setSource2Col] = useState(initialValues?.source2Col || '');
  const [targetTable, setTargetTable] = useState(initialValues?.targetTable || '');
  const [targetCol, setTargetCol] = useState(initialValues?.targetCol || '');
  const [joinType, setJoinType] = useState(initialValues?.joinType || 'LEFT');
  const [cardinality, setCardinality] = useState(initialValues?.cardinality || 'many-one');
  const [groupBy, setGroupBy] = useState(() => {
    const v = initialValues?.groupBy;
    return Array.isArray(v) ? v : v ? [v] : [];
  });
  const [operation, setOperation] = useState(initialValues?.operation || 'SUM');
  const [aggregateCol, setAggregateCol] = useState(initialValues?.aggregateCol || '');
  const [enableAggregate, setEnableAggregate] = useState(() =>
    !!(initialValues?.operation || initialValues?.aggregateType || initialValues?.aggregate_type)
  );

  const source1TableOptions = useMemo(
    () => sources.map((s) => ({ value: s.name || s.id, label: s.name || s.id })),
    [sources]
  );

  const source2TableOptions = useMemo(
    () =>
      sources
        .filter((s) => (s.name || s.id) !== source1Table)
        .map((s) => ({ value: s.name || s.id, label: s.name || s.id })),
    [sources, source1Table]
  );

  const targetTableOptions = useMemo(
    () => targets.map((t) => ({ value: t.tableName || t.id, label: t.tableName || t.id })),
    [targets]
  );

  const source1TableMeta = useMemo(
    () => sources.find((s) => (s.name || s.id) === source1Table),
    [sources, source1Table]
  );

  const source2TableMeta = useMemo(
    () => sources.find((s) => (s.name || s.id) === source2Table),
    [sources, source2Table]
  );

  const targetTableMeta = useMemo(
    () => targets.find((t) => (t.tableName || t.id) === targetTable),
    [targets, targetTable]
  );

  const source1ColOptions = useMemo(() => getColumnOptions(source1TableMeta), [source1TableMeta]);
  const source2ColOptions = useMemo(() => getColumnOptions(source2TableMeta), [source2TableMeta]);

  /** Target columns already mapped (from WebSocket or state) for the selected target table */
  const mappedTargetColsForTable = useMemo(() => {
    if (!targetTable || !Array.isArray(existingMappings)) return new Set();
    return new Set(
      existingMappings
        .filter((m) => (m.destination_name || m.destinationName) === targetTable)
        .map((m) => (m.target_column || m.targetColumn || '').trim())
        .filter(Boolean)
    );
  }, [targetTable, existingMappings]);

  const targetColOptions = useMemo(() => {
    if (!targetTable) return [];
    const target = targetTableMeta;
    const opts = target?.columns?.length ? getColumnOptions(target) : (fetchedHeader || [])
      .filter((h) => h.sourceTable === targetTable)
      .map((h) => ({ value: h.columnName ?? h.column_name, label: h.columnName ?? h.column_name }))
      .filter((o) => o.value != null && o.value !== '');
    const currentSelection = initialValues?.targetCol ?? targetCol;
    return opts.filter(
      (opt) => !mappedTargetColsForTable.has(opt.value) || opt.value === currentSelection
    );
  }, [targetTableMeta, targetTable, fetchedHeader, mappedTargetColsForTable, initialValues?.targetCol, targetCol]);

  const groupByOptions = useMemo(() => getColumnOptions(source1TableMeta), [source1TableMeta]);
  const aggregateColOptions = useMemo(() => getColumnOptions(source2TableMeta), [source2TableMeta]);

  const groupByValue = useMemo(
    () => (Array.isArray(groupBy) ? groupBy : groupBy != null && groupBy !== '' ? [groupBy] : []),
    [groupBy]
  );

  useEffect(() => {
    if (open) {
      setSource1Table(initialValues?.source1Table || initialValues?.sourceTable || '');
      setSource1Col(initialValues?.source1Col || initialValues?.sourceCol || '');
      setSource2Table(initialValues?.source2Table || '');
      setSource2Col(initialValues?.source2Col || '');
      setTargetTable(initialValues?.targetTable || '');
      setTargetCol(initialValues?.targetCol || '');
      setJoinType(initialValues?.joinType || 'LEFT');
      setCardinality(initialValues?.cardinality || 'many-one');
      const g = initialValues?.groupBy;
      setGroupBy(Array.isArray(g) ? g : g ? [g] : []);
      setOperation(initialValues?.operation || 'SUM');
      setAggregateCol(initialValues?.aggregateCol || '');
      setEnableAggregate(!!(initialValues?.operation || initialValues?.aggregateType || initialValues?.aggregate_type));
    }
  }, [open, initialValues]);

  const handleClose = () => {
    setSource1Table('');
    setSource1Col('');
    setSource2Table('');
    setSource2Col('');
    setTargetTable('');
    setTargetCol('');
    setJoinType('LEFT');
    setCardinality('many-one');
    setGroupBy('');
    setOperation('SUM');
    setAggregateCol('');
    setEnableAggregate(false);
    onClose();
  };

  const handleSave = () => {
    const baseOk =
      source1Table &&
      source1Col &&
      source2Table &&
      source2Col &&
      targetTable &&
      targetCol &&
      joinType &&
      cardinality;
    if (!baseOk) return;
    const groupByArr = enableAggregate ? groupByValue : [];
    const op = enableAggregate ? operation : '';
    const aggCol = enableAggregate ? aggregateCol : '';
    if (enableAggregate && (groupByArr.length === 0 || !op || !aggCol)) return;
    const source_columns = [
      `${source1Table}.${source1Col}`,
      `${source2Table}.${source2Col}`,
    ];
    const onStr = `${source1Table}.${source1Col} = ${source2Table}.${source2Col}`;
    onSave?.({
      source1Table,
      source1Col,
      source2Table,
      source2Col,
      source_columns,
      on: onStr,
      targetTable,
      targetCol,
      joinType,
      cardinality,
      groupBy: groupByArr,
      operation: op,
      aggregateCol: aggCol,
    });
    handleClose();
  };

  const canSave =
    source1Table &&
    source1Col &&
    source2Table &&
    source2Col &&
    targetTable &&
    targetCol &&
    joinType &&
    cardinality &&
    (!enableAggregate || (groupByValue.length > 0 && operation && aggregateCol));

  const sectionLabelSx = {
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: '#6b7280',
    textTransform: 'uppercase',
    mt: 1.5,
    mb: 1.5,
    display: 'block',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '1.2rem',
          color: '#1f2937',
          borderBottom: '1px solid #e5e7eb',
          py: 2,
          px: 3.5,
          backgroundColor: '#fff',
        }}
      >
        Join Mapping
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3.5,
          py: 3,
          backgroundColor: '#fff',
          overflow: 'auto',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* ─── JOIN CONFIGURATION ─── */}
        <Typography component="span" sx={sectionLabelSx}>
          Join configuration
        </Typography>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            mb: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Row 1: [Source 1 Table] [Col] = [Source 2 Table] [Col] */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  py: 0.5,
                }}
              >
                <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 140px', ...selectSx }}>
                  <InputLabel id="join-source1-table">Source 1 table</InputLabel>
                  <Select
                    labelId="join-source1-table"
                    value={source1Table}
                    label="Source 1 table"
                    MenuProps={selectMenuProps}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSource1Table(v);
                      setSource1Col('');
                      setGroupBy('');
                      if (source2Table === v) {
                        setSource2Table('');
                        setSource2Col('');
                        setAggregateCol('');
                      }
                    }}
                    disabled={reviewMode}
                  >
                    {source1TableOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130, flex: '1 1 110px', ...selectSx }}>
                  <InputLabel id="join-source1-col">Column</InputLabel>
                  <Select
                    labelId="join-source1-col"
                    value={source1Col}
                    label="Column"
                    MenuProps={selectMenuProps}
                    onChange={(e) => setSource1Col(e.target.value)}
                    disabled={reviewMode || !source1Table}
                  >
                    {source1ColOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  component="span"
                  sx={{
                    px: 1,
                    color: '#64748b',
                    fontWeight: 600,
                    fontSize: '1rem',
                    flexShrink: 0,
                  }}
                >
                  ON
                </Typography>
                <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 140px', ...selectSx }}>
                  <InputLabel id="join-source2-table">Source 2 table</InputLabel>
                  <Select
                    labelId="join-source2-table"
                    value={source2Table}
                    label="Source 2 table"
                    MenuProps={selectMenuProps}
                    onChange={(e) => {
                      setSource2Table(e.target.value);
                      setSource2Col('');
                      setAggregateCol('');
                    }}
                    disabled={reviewMode}
                  >
                    {source2TableOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130, flex: '1 1 110px', ...selectSx }}>
                  <InputLabel id="join-source2-col">Column</InputLabel>
                  <Select
                    labelId="join-source2-col"
                    value={source2Col}
                    label="Column"
                    MenuProps={selectMenuProps}
                    onChange={(e) => setSource2Col(e.target.value)}
                    disabled={reviewMode || !source2Table}
                  >
                    {source2ColOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            {/* Row 2: Target table & column */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 160px', ...selectSx }}>
                  <InputLabel id="join-target-table">Target table</InputLabel>
                  <Select
                    labelId="join-target-table"
                    value={targetTable}
                    label="Target table"
                    MenuProps={selectMenuProps}
                    onChange={(e) => {
                      setTargetTable(e.target.value);
                      setTargetCol('');
                    }}
                    disabled={reviewMode}
                  >
                    {targetTableOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 160px', ...selectSx }}>
                  <InputLabel id="join-target-col">Target column</InputLabel>
                  <Select
                    labelId="join-target-col"
                    value={targetCol ?? ''}
                    label="Target column"
                    MenuProps={selectMenuProps}
                    onChange={(e) => setTargetCol(e.target.value)}
                    disabled={reviewMode || !targetTable}
                  >
                    {targetTable && targetColOptions.length === 0 ? (
                      <MenuItem value="" disabled>
                        No columns available
                      </MenuItem>
                    ) : (
                      targetColOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            {/* Row 3: Join type & Cardinality */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel id="join-type">Join type</InputLabel>
                <Select
                  labelId="join-type"
                  value={joinType}
                  label="Join type"
                  onChange={(e) => setJoinType(e.target.value)}
                  disabled={reviewMode}
                >
                  {JOIN_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel id="join-cardinality">Cardinality</InputLabel>
                <Select
                  labelId="join-cardinality"
                  value={cardinality}
                  label="Cardinality"
                  MenuProps={selectMenuProps}
                  onChange={(e) => setCardinality(e.target.value)}
                  disabled={reviewMode}
                >
                  {CARDINALITY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  id="join-enable-aggregate"
                  checked={enableAggregate}
                  onChange={(e) => setEnableAggregate(e.target.checked)}
                  disabled={reviewMode}
                  size="small"
                  sx={{ color: NAGARRO_PRIMARY, '&.Mui-checked': { color: NAGARRO_PRIMARY }, p: 0.5, mr: 1 }}
                />
                <Typography
                  component="label"
                  htmlFor="join-enable-aggregate"
                  variant="body2"
                  sx={{ cursor: 'pointer', fontWeight: 500, color: '#374151' }}
                >
                  Add aggregation (use function)
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {enableAggregate && (
          <>
            <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />
            {/* ─── AGGREGATE CONFIGURATION ─── */}
            <Typography component="span" sx={sectionLabelSx}>
              Aggregate configuration
            </Typography>
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <InputLabel id="join-operation">Aggregate type</InputLabel>
                    <Select
                      labelId="join-operation"
                      value={operation}
                      label="Aggregate type"
                      onChange={(e) => setOperation(e.target.value)}
                      disabled={reviewMode}
                      MenuProps={{
                        PaperProps: { sx: { maxHeight: 320, maxWidth: '100%' } },
                      }}
                    >
                      {AGGREGATE_OPERATIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <InputLabel id="join-aggregate-col">Aggregate column</InputLabel>
                    <Select
                      labelId="join-aggregate-col"
                      value={aggregateCol}
                      label="Aggregate column"
                      MenuProps={selectMenuProps}
                      onChange={(e) => setAggregateCol(e.target.value)}
                      disabled={reviewMode || !source2Table}
                    >
                      {aggregateColOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <InputLabel id="join-groupby">Group by</InputLabel>
                    <Select
                      labelId="join-groupby"
                      multiple
                      value={groupByValue}
                      label="Group by"
                      onChange={(e) => setGroupBy(Array.isArray(e.target.value) ? e.target.value : [])}
                      disabled={reviewMode || !source1Table}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.25 }}>
                          {(Array.isArray(selected) ? selected : []).map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                              sx={{ height: 24, '& .MuiChip-label': { px: 1 }, backgroundColor: 'rgba(0, 4, 60, 0.08)', color: NAGARRO_PRIMARY }}
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={selectMenuProps}
                    >
                      {groupByOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Checkbox checked={groupByValue.indexOf(opt.value) > -1} size="small" sx={{ mr: 1 }} />
                          <ListItemText primary={opt.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#fafafa',
          position: 'sticky',
          bottom: 0,
          flexShrink: 0,
        }}
      >
        {reviewMode ? (
          <>
            <Button
              onClick={onReject}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                px: 3,
                bgcolor: '#dc2626',
                color: '#fff',
                '&:hover': { bgcolor: '#b91c1c' },
              }}
            >
              Reject
            </Button>
            <Button
              onClick={onApprove}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                px: 3,
                bgcolor: '#16a34a',
                color: '#fff',
                '&:hover': { bgcolor: '#15803d' },
              }}
            >
              Approve
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="inherit"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, px: 2.5 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!canSave}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                px: 3,
                bgcolor: '#00043c',
                color: '#fff',
                boxShadow: '0 2px 8px rgba(0, 4, 60, 0.35)',
                '&:hover': {
                  bgcolor: '#000335',
                  boxShadow: '0 4px 12px rgba(0, 4, 60, 0.4)',
                },
              }}
            >
              {isEdit ? 'Update' : 'Save'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default JoinWithAggregateDialog;
